import React, { useState, useEffect } from "react";
import type { FC, ReactNode, ReactElement } from "react";

type CarouselProps = {
	children: ReactNode[];
	slideTexts: (string | null)[];
	autoPlay: boolean;
	interval: number;
};

export const Carousel: FC<CarouselProps> = ({
	children,
	autoPlay = true,
	interval = 3000,
	slideTexts,
}) => {
	const [currentIndex, setCurrentIndex] = useState<number>(0);

	useEffect(() => {
		if (autoPlay) {
			const timer = setInterval(() => {
				setCurrentIndex((prevIndex) => (prevIndex + 1) % children.length);
			}, interval);

			return () => clearInterval(timer);
		}
	}, [autoPlay, interval, children.length]);

	return (
		<div className="relative w-full max-w-4xl mx-auto">
			<div className="relative overflow-hidden rounded-lg">
				<div
					className="flex transition-transform duration-500 ease-in-out"
					style={{
						transform: `translateX(-${currentIndex * 100}%)`,
					}}
				>
					{children.map((item, index) => (
						<div
							key={`slide-${(item as ReactElement).key || undefined}`}
							className="flex-shrink-0 w-full flex flex-col justify-center items-center"
						>
							{slideTexts[index] && (
								<p className="mb-6 text-xl text-white w-1/2 text-center">
									{slideTexts[index]}
								</p>
							)}
							{item}
						</div>
					))}
				</div>
			</div>
			<div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 flex space-x-2">
				{children.map((item, index) => (
					<span
						key={`dot-${(item as ReactElement).key || undefined}`}
						className={`h-2  ${currentIndex === index ? "w-10" : "w-3"} rounded-full cursor-pointer bg-white ${
							currentIndex === index ? "opacity-100" : "opacity-40"
						}`}
						onClick={() => setCurrentIndex(index)}
						onKeyUp={(e) => e.key === "Enter" && setCurrentIndex(index)}
						tabIndex={0}
						role="button"
						aria-label={`Go to slide ${index + 1}`}
					/>
				))}
			</div>
		</div>
	);
};
